import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  InputAdornment,
} from "@mui/material";
import { DraggablePaper } from "./DraggablePaper";
import { useDebouncedCallback } from "../utils/useDebouncedCallback";

// Memoize SkewDialog to prevent unnecessary re-renders
const SkewDialog = React.memo(({ open, onClose, canvas, selectedObject }) => {
  const [skewX, setSkewX] = useState(0);
  const [skewY, setSkewY] = useState(0);
  const [prevSkewX, setPrevSkewX] = useState(0);
  const [prevSkewY, setPrevSkewY] = useState(0);
  const [isDirty, setIsDirty] = useState(false);
  const [inputSkewX, setInputSkewX] = useState("");
  const [inputSkewY, setInputSkewY] = useState("");
  const selectedObjectRef = useRef(selectedObject);

  // Update ref when selectedObject changes
  useEffect(() => {
    selectedObjectRef.current = selectedObject;
    console.log("[SkewDialog] selectedObject updated:", selectedObject);
  }, [selectedObject]);

  // Initialize skew values when dialog opens or selected object changes
  useEffect(() => {
    if (selectedObject && open) {
      const currentSkewX = Math.round(Number(selectedObject.get("skewX")) || 0);
      const currentSkewY = Math.round(Number(selectedObject.get("skewY")) || 0);
      setSkewX(currentSkewX);
      setSkewY(currentSkewY);
      setInputSkewX(currentSkewX.toString());
      setInputSkewY(currentSkewY.toString());
      setPrevSkewX(currentSkewX);
      setPrevSkewY(currentSkewY);
      setIsDirty(false);
      console.log(
        "[SkewDialog] Initialized skewX:",
        currentSkewX,
        "skewY:",
        currentSkewY
      );
    }
  }, [selectedObject, open]);

  // Memoize update callbacks
  const updateSkewXCallback = useCallback(
    (value) => {
      const roundedValue = Number(value);
      setSkewX(roundedValue);
      setInputSkewX(roundedValue.toString());
      if (selectedObjectRef.current && canvas) {
        canvas.setActiveObject(selectedObjectRef.current);
        selectedObjectRef.current.set({ skewX: roundedValue });
        selectedObjectRef.current.setCoords();
        canvas.requestRenderAll();
        setIsDirty(true);
        console.log(
          "[SkewDialog] Updated skewX:",
          roundedValue,
          "Object:",
          selectedObjectRef.current
        );
      } else {
        console.warn("[SkewDialog] Update failed:", {
          selectedObject: selectedObjectRef.current,
          canvas,
        });
      }
    },
    [canvas]
  );

  const updateSkewYCallback = useCallback(
    (value) => {
      const roundedValue = Number(value);
      setSkewY(roundedValue);
      setInputSkewY(roundedValue.toString());
      if (selectedObjectRef.current && canvas) {
        canvas.setActiveObject(selectedObjectRef.current);
        selectedObjectRef.current.set({ skewY: roundedValue });
        selectedObjectRef.current.setCoords();
        canvas.requestRenderAll();
        setIsDirty(true);
        console.log(
          "[SkewDialog] Updated skewY:",
          roundedValue,
          "Object:",
          selectedObjectRef.current
        );
      } else {
        console.warn("[SkewDialog] Update failed:", {
          selectedObject: selectedObjectRef.current,
          canvas,
        });
      }
    },
    [canvas]
  );

  // Create debounced update functions
  const updateSkewX = useDebouncedCallback(updateSkewXCallback, 100, canvas);
  const updateSkewY = useDebouncedCallback(updateSkewYCallback, 100, canvas);

  // Handle dialog close (cancel)
  const handleCancel = useCallback(() => {
    if (isDirty && selectedObject) {
      selectedObject.set({ skewX: prevSkewX, skewY: prevSkewY });
      selectedObject.setCoords();
      canvas?.requestRenderAll();
      console.log(
        "[SkewDialog] Canceled, reverted to skewX:",
        prevSkewX,
        "skewY:",
        prevSkewY
      );
    }
    onClose();
  }, [isDirty, selectedObject, prevSkewX, prevSkewY, canvas, onClose]);

  // Handle apply skew (save changes and add to history)
  const handleApply = useCallback(() => {
    updateSkewX.flush();
    updateSkewY.flush();
    if (isDirty && canvas && selectedObject) {
      canvas.setActiveObject(selectedObject);
      selectedObject.set({ skewX, skewY });
      selectedObject.setCoords();
      canvas.requestRenderAll();
      canvas.fire("object:modified", { target: selectedObject });
      console.log(
        "[SkewDialog] Applied skewX:",
        skewX,
        "skewY:",
        skewY,
        "Fired object:modified for:",
        selectedObject
      );
    } else {
      console.log(
        "[SkewDialog] Apply skipped: isDirty=",
        isDirty,
        "canvas=",
        !!canvas,
        "selectedObject=",
        !!selectedObject
      );
    }
    setIsDirty(false);
    onClose();
  }, [
    isDirty,
    canvas,
    selectedObject,
    skewX,
    skewY,
    updateSkewX,
    updateSkewY,
    onClose,
  ]);

  // Stop mouse events from propagating to allow input interaction
  const stopPropagation = useCallback((e) => {
    e.stopPropagation();
  }, []);

  // Prevent backdrop click from closing the dialog
  const handleBackdropClick = useCallback((e) => {
    e.stopPropagation();
  }, []);

  // Prevent keydown events (e.g., Escape key) from closing the dialog
  const handleKeyDown = useCallback((e) => {
    if (e.key === "Escape") {
      e.stopPropagation();
    }
  }, []);

  // Handle input changes
  const handleSkewXChange = useCallback((e) => {
    setInputSkewX(e.target.value);
  }, []);

  const handleSkewXBlur = useCallback(() => {
    const value = Number(inputSkewX);
    if (!isNaN(value)) {
      updateSkewX(value);
    } else {
      setInputSkewX(skewX.toString());
    }
  }, [inputSkewX, skewX, updateSkewX]);

  const handleSkewYChange = useCallback((e) => {
    setInputSkewY(e.target.value);
  }, []);

  const handleSkewYBlur = useCallback(() => {
    const value = Number(inputSkewY);
    if (!isNaN(value)) {
      updateSkewY(value);
    } else {
      setInputSkewY(skewY.toString());
    }
  }, [inputSkewY, skewY, updateSkewY]);

  const handleSkewXStep = useCallback(
    (e) => {
      const value = Number(e.target.value);
      if (!isNaN(value)) {
        updateSkewX(value);
      }
    },
    [updateSkewX]
  );

  const handleSkewYStep = useCallback(
    (e) => {
      const value = Number(e.target.value);
      if (!isNaN(value)) {
        updateSkewY(value);
      }
    },
    [updateSkewY]
  );

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        if (reason === "backdropClick" || reason === "escapeKeyDown") {
          return;
        }
        handleCancel();
      }}
      PaperComponent={DraggablePaper}
      aria-labelledby="draggable-dialog-title"
      BackdropProps={{
        style: { backgroundColor: "rgba(0, 0, 0, 0.2)" },
        onClick: handleBackdropClick,
      }}
      onKeyDown={handleKeyDown}
      sx={{
        "& .MuiDialog-paper": {
          backgroundColor: "#fff",
          boxShadow: "0px 3px 5px rgba(0, 0, 0, 0.2)",
        },
      }}
    >
      <DialogTitle
        id="draggable-dialog-title"
        sx={{
          color: "#ffffff",
          backgroundColor: "#0d2445",
          fontSize: "20px",
          display: "flex",
          alignItems: "center",
          cursor: "move",
        }}
      >
        <svg
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          style={{ marginRight: "8px" }}
        >
          <circle cx="4" cy="4" r="1.5" fill="#ffffff" />
          <circle cx="8" cy="4" r="1.5" fill="#ffffff" />
          <circle cx="4" cy="8" r="1.5" fill="#ffffff" />
          <circle cx="8" cy="8" r="1.5" fill="#ffffff" />
          <circle cx="4" cy="12" r="1.5" fill="#ffffff" />
          <circle cx="8" cy="12" r="1.5" fill="#ffffff" />
        </svg>
        Skew Toolbar
      </DialogTitle>
      <DialogContent
        onMouseDown={stopPropagation}
        onMouseMove={stopPropagation}
        onMouseUp={stopPropagation}
        onMouseLeave={stopPropagation}
      >
        <Stack direction="column" sx={{ marginTop: 1 }}>
          <TextField
            type="number"
            id="canvasSkewX"
            margin="dense"
            value={inputSkewX}
            onChange={handleSkewXChange}
            onBlur={handleSkewXBlur}
            onInput={handleSkewXStep}
            InputLabelProps={{ shrink: true }}
            InputProps={{
              endAdornment: <InputAdornment position="end">deg</InputAdornment>,
              sx: {
                bgcolor: "#fff",
                height: 36,
                width: 122,
                paddingTop: 1,
                marginRight: 1,
              },
              inputProps: { step: 1 }, // Step 1deg for fine control
            }}
            label="Skew X"
            sx={{
              "& .MuiInputLabel-root": {
                fontFamily: "Roboto",
                fontWeight: 500,
                marginTop: 1,
              },
            }}
            variant="outlined"
            inputProps={{ "aria-label": "Skew X in degrees" }}
          />
          <TextField
            type="number"
            id="canvasSkewY"
            margin="dense"
            value={inputSkewY}
            onChange={handleSkewYChange}
            onBlur={handleSkewYBlur}
            onInput={handleSkewYStep}
            InputLabelProps={{ shrink: true }}
            InputProps={{
              endAdornment: <InputAdornment position="end">deg</InputAdornment>,
              sx: {
                bgcolor: "#fff",
                height: 36,
                width: 122,
                paddingTop: 1,
                marginRight: 1,
              },
              inputProps: { step: 1 }, // Step 1deg for fine control
            }}
            label="Skew Y"
            sx={{
              "& .MuiInputLabel-root": {
                fontFamily: "Roboto",
                fontWeight: 500,
                marginTop: 1,
              },
            }}
            variant="outlined"
            inputProps={{ "aria-label": "Skew Y in degrees" }}
          />
        </Stack>
      </DialogContent>
      <DialogActions
        onMouseDown={stopPropagation}
        onMouseMove={stopPropagation}
        onMouseUp={stopPropagation}
        onMouseLeave={stopPropagation}
      >
        <Button
          onClick={handleCancel}
          color="secondary"
          aria-label="Cancel skew changes"
        >
          Cancel
        </Button>
        <Button
          onClick={handleApply}
          color="primary"
          aria-label="Apply skew changes"
        >
          Apply Skew
        </Button>
      </DialogActions>
    </Dialog>
  );
});

export default SkewDialog;
